import { useState, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import StarRating from 'components/Shared/StarRating';
import ToggleExpandedButton from 'components/Dealer/ToggleExpandedButton';
import isElementOverflowing from 'lib/isElementOverflowing';
import { trackDealerPagesClick } from 'lib/Tracking/DealerPages';
import { DealerPagesEventType } from 'lib/EventSchema';
import TrackingDataContext from 'contexts/TrackingDataContext';
import styles from './styles';

function Title({ title, author }) {
  const titleMarkup = title ? (
    <span>
      "{title}"
      <style jsx>
        {`
          font-weight: 900;
        `}
      </style>
    </span>
  ) : null;
  const authorMarkup = author ? (
    <span>
      {author}
      <style jsx>
        {`
          font-weight: 600;
        `}
      </style>
    </span>
  ) : null;
  const seperatorMarkup =
    title && author ? (
      <span>
        -{' '}
        <style jsx>
          {`
            font-weight: 600;
          `}
        </style>
      </span>
    ) : null;

  return (
    <header>
      {titleMarkup}
      {seperatorMarkup}
      {authorMarkup}
    </header>
  );
}

Title.propTypes = {
  title: PropTypes.string,
  author: PropTypes.string,
};

export default function Review({
  writtenAt,
  overallRating,
  title,
  author,
  content,
}) {
  const { t } = useTranslation();
  const { dealerPageUrl } = useContext(TrackingDataContext);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [startedOverflowing, setStartedOverflowing] = useState(undefined);
  const contentRef = useRef(null);

  useEffect(() => {
    if (typeof startedOverflowing === 'undefined') {
      setStartedOverflowing(isElementOverflowing(contentRef.current));
    }
  }, [startedOverflowing]);

  function onClick() {
    if (isCollapsed) {
      trackDealerPagesClick({
        type: DealerPagesEventType.read_more_review_click,
        dealerPageUrl,
      });
    }

    setIsCollapsed(!isCollapsed);
  }

  const visibilityClass = isCollapsed ? 'collapsed' : '';

  return (
    <div className="review-container" data-testid="review">
      <div className="layout-container">
        <div className="summary">
          <div className="date">{writtenAt}</div>
          <div className="overall-rating">
            <StarRating value={overallRating} />
          </div>
          <div className="overall-rating-label">
            {t('dealerPages.overallRating')}
          </div>
        </div>
        <div className="review">
          <Title title={title} author={author} />
          <div className={`content ${visibilityClass}`} ref={contentRef}>
            {content}
          </div>
          {startedOverflowing && (
            <ToggleExpandedButton
              onClick={onClick}
              className={`expand toggle-expanded-button`}
              isExpanded={!isCollapsed}
            />
          )}
        </div>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

Review.propTypes = {
  id: PropTypes.number.isRequired,
  writtenAt: PropTypes.string.isRequired,
  overallRating: PropTypes.number.isRequired,
  author: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string.isRequired,
};
